if (!customElements.get('audio-player')) {
  customElements.define(
    'audio-player',
    class AudioPlayer extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        this.audio = this.querySelector('[data-audio]');
        this.toggle = this.querySelector('[data-audio-toggle]');
        this.track = this.querySelector('[data-audio-track]');
        this.fill = this.querySelector('[data-audio-fill]');
        this.thumb = this.querySelector('[data-audio-thumb]');
        this.time = this.querySelector('[data-audio-time]');
        this.playIcon = this.querySelector('[data-audio-icon-play]');
        this.pauseIcon = this.querySelector('[data-audio-icon-pause]');

        if (!this.audio) return;

        this.controller = new AbortController();
        const { signal } = this.controller;

        this.toggle?.addEventListener('click', this.onToggle, { signal });
        this.track?.addEventListener('pointerdown', this.onSeekStart, { signal });
        this.track?.addEventListener('keydown', this.onKeydown, { signal });
        this.audio.addEventListener('play', this.onPlay, { signal });
        this.audio.addEventListener('pause', this.onPause, { signal });
        this.audio.addEventListener('timeupdate', this.onTimeUpdate, { signal });
        this.audio.addEventListener('loadedmetadata', this.onTimeUpdate, { signal });
        // The length of a stream is often only known once enough of it has
        // been fetched, and the bar cannot be positioned before then.
        this.audio.addEventListener('durationchange', this.onTimeUpdate, { signal });
        this.audio.addEventListener('progress', this.onTimeUpdate, { signal });
        this.audio.addEventListener('ended', this.onEnded, { signal });

        this.render(0);
      }

      disconnectedCallback() {
        // two-column-layout re-parents these blocks at runtime, which
        // disconnects and immediately reconnects the element. Only a real
        // removal should tear the player down and stop playback.
        setTimeout(() => {
          if (this.isConnected) return;
          this.controller?.abort();
          this.controller = null;
          this.audio?.pause();
        });
      }

      onToggle = () => {
        if (this.audio.paused) {
          // Only one sample should ever be audible at a time.
          document.querySelectorAll('audio-player').forEach((player) => {
            if (player !== this) player.pauseAudio();
          });
          this.audio.play().catch(() => this.onPause());
        } else {
          this.audio.pause();
        }
      };

      pauseAudio() {
        this.audio?.pause();
      }

      onPlay = () => this.renderPlayState(true);

      onPause = () => this.renderPlayState(false);

      /**
       * The icons are SVG elements, and `hidden` is only reflected as a
       * property on HTML elements, so the attribute has to be set directly.
       */
      renderPlayState(playing) {
        this.playIcon?.toggleAttribute('hidden', playing);
        this.pauseIcon?.toggleAttribute('hidden', !playing);
        this.toggle?.setAttribute('aria-pressed', playing ? 'true' : 'false');
      }

      onEnded = () => {
        this.audio.currentTime = 0;
        this.onPause();
        this.render(0);
      };

      onTimeUpdate = () => {
        const duration = this.duration;
        this.render(duration ? this.audio.currentTime / duration : 0);
      };

      onSeekStart = (event) => {
        if (!this.duration) return;

        // Capturing on the track keeps the drag alive outside its bounds.
        this.track.setPointerCapture(event.pointerId);
        this.track.addEventListener('pointermove', this.onSeekMove);
        this.track.addEventListener('pointerup', this.onSeekEnd);
        this.track.addEventListener('pointercancel', this.onSeekEnd);
        this.seekTo(event);
      };

      onSeekMove = (event) => this.seekTo(event);

      onSeekEnd = (event) => {
        this.track.releasePointerCapture(event.pointerId);
        this.track.removeEventListener('pointermove', this.onSeekMove);
        this.track.removeEventListener('pointerup', this.onSeekEnd);
        this.track.removeEventListener('pointercancel', this.onSeekEnd);
      };

      onKeydown = (event) => {
        const duration = this.duration;
        if (!duration) return;

        const step = 5;
        let target = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowUp':
            target = this.audio.currentTime + step;
            break;
          case 'ArrowLeft':
          case 'ArrowDown':
            target = this.audio.currentTime - step;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = duration;
            break;
          case ' ':
          case 'Enter':
            event.preventDefault();
            this.onToggle();
            return;
          default:
            return;
        }

        event.preventDefault();
        this.audio.currentTime = Math.min(Math.max(target, 0), duration);
      };

      seekTo(event) {
        const bounds = this.track.getBoundingClientRect();
        if (!bounds.width) return;

        const ratio = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
        this.audio.currentTime = ratio * this.duration;
      }

      /**
       * `duration` stays Infinity for streamed responses and for MP3s without
       * a length header until the whole file has been fetched, which would
       * otherwise pin the progress bar at zero for the entire sample. The
       * seekable range is the best length the browser can offer until then.
       */
      get duration() {
        const reported = this.audio?.duration;
        if (Number.isFinite(reported) && reported > 0) return reported;

        const seekable = this.audio?.seekable;
        if (seekable?.length) {
          const end = seekable.end(seekable.length - 1);
          if (Number.isFinite(end) && end > 0) return end;
        }

        return 0;
      }

      render(ratio) {
        const percent = `${ratio * 100}%`;
        if (this.fill) this.fill.style.width = percent;
        if (this.thumb) this.thumb.style.left = percent;

        if (this.track) {
          this.track.setAttribute('aria-valuenow', Math.round(ratio * 100));
          this.track.setAttribute('aria-valuetext', this.formatTime(this.audio.currentTime));
        }

        if (this.time) this.time.textContent = this.label();
      }

      label() {
        const elapsed = this.audio.currentTime || 0;
        const duration = this.duration;

        switch (this.dataset.timeDisplay) {
          case 'remaining':
            return `-${this.formatTime(Math.max(duration - elapsed, 0))}`;
          case 'elapsed_duration':
            return `${this.formatTime(elapsed)} / ${this.formatTime(duration)}`;
          default:
            return this.formatTime(elapsed);
        }
      }

      formatTime(seconds) {
        const total = Math.floor(seconds || 0);
        const minutes = Math.floor(total / 60);
        const remainder = total % 60;
        return `${minutes}:${remainder < 10 ? '0' : ''}${remainder}`;
      }
    }
  );
}
